// src/Stores/CountryStore.ts
import { create } from "zustand";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { makeRequest } from "@/helpers/apiWrapper";

type Country = {
  id: string;
  name: string;
  alpha3: string;
  dial_code: string;
  maxLength: number;
  file_url: string;
};

type CountryStore = {
  countries: Country[];
  ipCountry: string | null;
  lastCountry: string | null;
  setCountries: (countries: Country[]) => void;
  setIpCountry: (alpha3: string) => void;
  setLastCountry: (alpha3: string) => void;
};

export const useCountryStore = create<CountryStore>((set) => ({
  countries: [],
  ipCountry: null,
  lastCountry: localStorage.getItem("last_country") || null,
  setLastCountry: (alpha3) => {
    localStorage.setItem("last_country", alpha3);
    set({ lastCountry: alpha3 });
  },
  setIpCountry: (alpha3) => {
    localStorage.setItem("ip_country", alpha3);
    set({ ipCountry: alpha3 });
  },
  setCountries: (countries) => set({ countries }),
}));

export const useCountriesQuery = () => {
  const setCountries = useCountryStore((state) => state.setCountries);

  const { data, isLoading } = useQuery({
    queryKey: ["countries"],
    queryFn: async () => {
      const res = await makeRequest({
        endpoint: "/api/country/get-all-active-country",
        method: "GET",
        server: "main",
        isPublic: true,
        isEncrypted: false,
        isDecrypted: true,
      });

      return res;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });

  useEffect(() => {
    if (data?.Countries) {
      const formatted = data.Countries.map((c) => ({
        id: c._id,
        name: c.country_name,
        alpha3: c.country_iso_code,
        dial_code: c.dial_code || "",
        maxLength: c.maxLength || 10,
        file_url: c.file_url || "",
      }));
      setCountries(formatted);
    }
  }, [data, setCountries]);

  return { data: data?.Countries || [], isLoading };
};
